import { IJkitTourConfig, IJkitTourStep, TJkitTourBreakpoint, TJkitTourEntry } from './tour.model';

export interface IJkitResponsiveTourConfig {
	id: string;
	entry: TJkitTourEntry;
	priority?: number;
	suppressReplay?: boolean;
	variants: Partial<Record<TJkitTourBreakpoint, readonly IJkitTourStep[]>> & {
		default?: readonly IJkitTourStep[];
	};
}

export type TJkitTourDefinition = IJkitTourConfig | readonly IJkitTourConfig[] | IJkitResponsiveTourConfig;

export class JkitTourBuilder {
	private static readonly _breakpointOrder: readonly TJkitTourBreakpoint[] = ['desktop', 'mobile'];

	private static _isResponsiveTourConfig(definition: TJkitTourDefinition): definition is IJkitResponsiveTourConfig {
		return !Array.isArray(definition) && 'variants' in definition;
	}

	public static responsiveTour(config: IJkitResponsiveTourConfig): IJkitTourConfig[] {
		const tours: IJkitTourConfig[] = [];

		for (const breakpoint of JkitTourBuilder._breakpointOrder) {
			const steps = config.variants[breakpoint];
			if (!steps?.length) continue;

			tours.push({
				id: config.id,
				entry: config.entry,
				breakpoint,
				priority: config.priority,
				suppressReplay: config.suppressReplay,
				steps
			});
		}

		const defaultSteps = config.variants.default;
		if (defaultSteps?.length) {
			tours.push({
				id: config.id,
				entry: config.entry,
				priority: config.priority,
				suppressReplay: config.suppressReplay,
				steps: defaultSteps
			});
		}

		return tours;
	}

	public static defineTours(...definitions: readonly TJkitTourDefinition[]): IJkitTourConfig[] {
		return definitions.flatMap(definition => {
			if (Array.isArray(definition)) return definition;
			if (JkitTourBuilder._isResponsiveTourConfig(definition)) return JkitTourBuilder.responsiveTour(definition);
			return [definition];
		});
	}
}

export const responsiveJkitTour = (config: IJkitResponsiveTourConfig): IJkitTourConfig[] => JkitTourBuilder.responsiveTour(config);

export const defineJkitTours = (...definitions: readonly TJkitTourDefinition[]): IJkitTourConfig[] =>
	JkitTourBuilder.defineTours(...definitions);
